import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckCircleIcon, ClockIcon, ShieldIcon, XCircleIcon } from 'lucide-react';
import { Language } from '../App';
interface ConsultationFormProps {
  language: Language;
}
const content = {
  en: {
    title: 'Request Your Private Consultation',
    subtitle: 'Fill out the form below and our team will contact you within 24 hours',
    fields: {
      name: 'Full Name',
      phone: 'Phone Number',
      language: 'Preferred Language',
      service: 'Service of Interest',
      message: 'Message (Optional)'
    },
    languageOptions: ['English', 'Español'],
    serviceOptions: ['Liposuction', 'Tummy Tuck', 'Breast Surgery', 'Facelift & Eyelid Surgery', 'Injectables & Fillers', 'Body Contouring / BBL', 'General Consultation', 'Follow-up Appointment', 'Other'],
    submit: 'Submit Request',
    disclaimer: 'Submitting this form does not confirm an appointment. A coordinator will contact you to schedule.',
    trust: [{
      icon: ClockIcon,
      text: '24h Response'
    }, {
      icon: ShieldIcon,
      text: '100% Confidential'
    }, {
      icon: XCircleIcon,
      text: 'No Obligation'
    }],
    success: {
      title: 'Thank You!',
      message: 'Thank you for your information. Our team will contact you within 24 hours to schedule your consultation.'
    }
  },
  es: {
    title: 'Solicite Su Consulta Privada',
    subtitle: 'Complete el formulario a continuación y nuestro equipo se comunicará con usted en 24 horas',
    fields: {
      name: 'Nombre Completo',
      phone: 'Número de Teléfono',
      language: 'Idioma Preferido',
      service: 'Servicio de Interés',
      message: 'Mensaje (Opcional)'
    },
    languageOptions: ['English', 'Español'],
    serviceOptions: ['Liposucción', 'Abdominoplastia', 'Cirugía de Senos', 'Lifting Facial y Blefaroplastia', 'Inyectables y Rellenos', 'Contorno Corporal / BBL', 'Consulta General', 'Cita de Seguimiento', 'Otro'],
    submit: 'Enviar Solicitud',
    disclaimer: 'Enviar este formulario no confirma una cita. Un coordinador se comunicará con usted para programar.',
    trust: [{
      icon: ClockIcon,
      text: 'Respuesta en 24h'
    }, {
      icon: ShieldIcon,
      text: '100% Confidencial'
    }, {
      icon: XCircleIcon,
      text: 'Sin Obligación'
    }],
    success: {
      title: '¡Gracias!',
      message: 'Gracias por su información. Nuestro equipo se comunicará con usted en 24 horas para programar su consulta.'
    }
  }
};
export function ConsultationForm({
  language
}: ConsultationFormProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = content[language];
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    language: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  if (submitted) {
    return <section className="py-24 px-4 sm:px-6 lg:px-8 bg-cream" data-id="element-29">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5
      }} className="max-w-2xl mx-auto text-center space-y-6" data-id="element-30">
          <div className="w-20 h-20 bg-gradient-to-r from-rose-400 to-rose-500 rounded-full flex items-center justify-center mx-auto" data-id="element-31">
            <CheckCircleIcon className="w-10 h-10 text-white" data-id="element-32" />
          </div>
          <h3 className="text-3xl font-bold text-espresso" data-id="element-33">
            {t.success.title}
          </h3>
          <p className="text-espresso/70 text-lg" data-id="element-34">{t.success.message}</p>
          <button onClick={() => setSubmitted(false)} className="text-rose-500 font-medium hover:underline" data-id="element-35">
            {language === 'en' ? 'Submit Another Request' : 'Enviar Otra Solicitud'}
          </button>
        </motion.div>
      </section>;
  }
  return <section ref={ref} className="py-24 px-4 sm:px-6 lg:px-8 bg-cream" data-id="element-36">
      <div className="max-w-7xl mx-auto" data-id="element-37">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16" data-id="element-38">
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} className="lg:col-span-2 space-y-6" data-id="element-39">
            <h2 className="text-4xl sm:text-5xl font-bold text-espresso" data-id="element-40">
              {t.title}
            </h2>
            <p className="text-lg text-espresso/70" data-id="element-41">{t.subtitle}</p>

            <div className="space-y-4 pt-8" data-id="element-42">
              {t.trust.map((item, index) => {
              const Icon = item.icon;
              return <motion.div key={item.text} initial={{
                opacity: 0,
                x: -30
              }} animate={isInView ? {
                opacity: 1,
                x: 0
              } : {}} transition={{
                duration: 0.5,
                delay: 0.2 + index * 0.1
              }} className="flex items-center gap-3" data-id="element-43">
                    <div className="bg-pearl/20 rounded-full p-2" data-id="element-44">
                      <Icon className="w-5 h-5 text-espresso" data-id="element-45" />
                    </div>
                    <span className="text-espresso font-medium" data-id="element-46">
                      {item.text}
                    </span>
                  </motion.div>;
            })}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 50
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-3" data-id="element-47">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-xl space-y-6" data-id="element-48">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.3
            }} data-id="element-49">
                <label className="block text-sm font-semibold text-espresso mb-2" data-id="element-50">
                  {t.fields.name} *
                </label>
                <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-pearl/30 focus:border-rose-400 focus:ring-2 focus:ring-rose-400/20 outline-none transition-all" data-id="element-51" />
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.4
            }} data-id="element-52">
                <label className="block text-sm font-semibold text-espresso mb-2" data-id="element-53">
                  {t.fields.phone} *
                </label>
                <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-pearl/30 focus:border-rose-400 focus:ring-2 focus:ring-rose-400/20 outline-none transition-all" data-id="element-54" />
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.5
            }} data-id="element-55">
                <label className="block text-sm font-semibold text-espresso mb-2" data-id="element-56">
                  {t.fields.language}
                </label>
                <select name="language" value={formData.language} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-pearl/30 focus:border-rose-400 focus:ring-2 focus:ring-rose-400/20 outline-none transition-all" data-id="element-57">
                  <option value="" data-id="element-58">
                    {language === 'en' ? 'Select...' : 'Seleccionar...'}
                  </option>
                  {t.languageOptions.map(opt => <option key={opt} value={opt} data-id="element-59">
                      {opt}
                    </option>)}
                </select>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.6
            }} data-id="element-60">
                <label className="block text-sm font-semibold text-espresso mb-2" data-id="element-61">
                  {t.fields.service}
                </label>
                <select name="service" value={formData.service} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-pearl/30 focus:border-rose-400 focus:ring-2 focus:ring-rose-400/20 outline-none transition-all" data-id="element-62">
                  <option value="" data-id="element-63">
                    {language === 'en' ? 'Select...' : 'Seleccionar...'}
                  </option>
                  {t.serviceOptions.map(opt => <option key={opt} value={opt} data-id="element-64">
                      {opt}
                    </option>)}
                </select>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.7
            }} data-id="element-65">
                <label className="block text-sm font-semibold text-espresso mb-2" data-id="element-66">
                  {t.fields.message}
                </label>
                <textarea name="message" rows={4} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-pearl/30 focus:border-rose-400 focus:ring-2 focus:ring-rose-400/20 outline-none transition-all resize-none" data-id="element-67" />
              </motion.div>

              <motion.button initial={{
              opacity: 0,
              y: 20
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.8
            }} type="submit" className="w-full bg-gradient-to-r from-rose-400 to-rose-500 text-white py-4 rounded-lg font-semibold hover:shadow-lg hover:shadow-rose-500/50 transition-all duration-300 hover:scale-[1.02]" data-id="element-68">
                {t.submit}
              </motion.button>

              <motion.p initial={{
              opacity: 0
            }} animate={isInView ? {
              opacity: 1
            } : {}} transition={{
              duration: 0.5,
              delay: 0.9
            }} className="text-xs text-espresso/60 text-center" data-id="element-69">
                {t.disclaimer}
              </motion.p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>;
}